import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["use", "children", "borderRadius", "borderStartStartRadius", "borderStartEndRadius", "borderEndEndRadius", "borderEndStartRadius"];
import { getBackgroundColorToken, getBadgeStyleOverrides } from './Badge.helpers';
import styled, { isTrellisTheme } from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { useBadgeContext } from './BadgeProvider';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useTheme } from 'foundations-theming/utils';
import { LINE_HEIGHT, FONT_SIZE, SIZE, MIN_HEIGHT } from '../../../constants/nonTokenisedValues';
import { jsx as _jsx } from "react/jsx-runtime";
export const StyledBadge = styled.span.withConfig({
  displayName: "Badge__StyledBadge"
})(({
  $cssStyles,
  $styleOverrides,
  $use,
  theme
}) => {
  const {
    fontFamily,
    fontWeight
  } = theme.typography['heading-25'];
  const isTrellis = isTrellisTheme(theme);
  return Object.assign({}, $cssStyles, {
    textAlign: 'center'
  }, $use === 'count' || $use === 'dot' ? Object.assign({
    display: 'inline-block',
    backgroundColor: theme.color['fill-transitional-alert'],
    border: `${theme.borderWidth['200']} solid ${theme.color['border-core-on-fill-default']}`,
    borderStartStartRadius: theme.borderRadius['full'],
    borderStartEndRadius: theme.borderRadius['full'],
    borderEndEndRadius: theme.borderRadius['full'],
    borderEndStartRadius: theme.borderRadius['full'],
    pointerEvents: 'none',
    verticalAlign: 'baseline'
  }, $use === 'count' && {
    fontFamily,
    fontWeight,
    color: theme.color['text-core-on-fill-default'],
    paddingBlock: `${theme.space['0']}`,
    paddingInline: `${theme.space['100']}`,
    // no tokens for the below properties
    fontSize: '11px',
    lineHeight: '19px',
    // not available on Figma but necessary to match the design
    minBlockSize: SIZE.BADGE_MIN_SIZE,
    minInlineSize: SIZE.BADGE_MIN_SIZE
  }, $use === 'dot' && {
    blockSize: SIZE.BADGE_DOT,
    inlineSize: SIZE.BADGE_DOT,
    textIndent: '-1000rem'
  }) : {
    fontFamily,
    fontWeight,
    display: 'inline-flex',
    textTransform: isTrellis ? 'capitalize' : 'uppercase',
    verticalAlign: 'inherit',
    hyphens: 'manual',
    borderStartStartRadius: theme.borderRadius['transitional-full-3'],
    borderStartEndRadius: theme.borderRadius['transitional-full-3'],
    borderEndEndRadius: theme.borderRadius['transitional-full-3'],
    borderEndStartRadius: theme.borderRadius['transitional-full-3'],
    minHeight: MIN_HEIGHT.BADGE,
    backgroundColor: theme.color[getBackgroundColorToken($use)],
    color: theme.color[$use === 'koala' || $use === 'blue' || $use === 'teal' || $use === 'green' || $use === 'yellow' || $use === 'orange' || $use === 'magenta' || $use === 'purple' ? 'text-core-default' : $use === 'marigold' ? 'text-inverse-default' : 'text-core-on-fill-default'],
    paddingBlock: `${theme.space['0']}`,
    paddingInline: `${theme.space['200']}`,
    lineHeight: LINE_HEIGHT.BADGE,
    fontSize: FONT_SIZE.BADGE
  }, $styleOverrides);
});
export const Badge = _ref => {
  let {
      use = 'free',
      children,
      borderRadius,
      borderStartStartRadius,
      borderStartEndRadius,
      borderEndEndRadius,
      borderEndStartRadius
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const theme = useTheme();
  const badgeContext = useBadgeContext();
  const badgeStyleOverrides = getBadgeStyleOverrides({
    use,
    theme,
    borderRadius,
    borderStartStartRadius,
    borderStartEndRadius,
    borderEndStartRadius,
    borderEndEndRadius,
    badgeContext
  });
  return /*#__PURE__*/_jsx(StyledBadge, Object.assign({
    $cssStyles: cssStyles,
    $styleOverrides: badgeStyleOverrides,
    $use: use
  }, domProps, {
    children: /*#__PURE__*/_jsx("span", {
      children: children
    })
  }));
};
Badge.displayName = 'Badge';